import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { inr, statusColor } from "@/lib/utils-format";
import {
  LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { DeferredChart } from "@/components/DeferredChart";
import { TrendingUp, Users, UserPlus, CalendarClock, IndianRupee, ListTodo, Sparkles, Loader2, AlertTriangle, Zap, Store, Banknote, Smartphone, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";

const StatCard = ({ label, value, icon: Icon, hint, testid, accent }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#EAE5D9] rounded-lg p-5 hover-lift"
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="uc-label">{label}</div>
        <div className="font-[Outfit] text-2xl font-semibold mt-1 tracking-tight">{value}</div>
        {hint && <div className="text-xs text-[#8B7355] mt-1">{hint}</div>}
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent || "bg-[#FEEBC8] text-[#C05621]"}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const MiniStat = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-[#FDFBF7] border border-[#EAE5D9] rounded-md p-3" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#C05621]" />
      <div className="uc-label">{label}</div>
    </div>
    <div className="font-[Outfit] text-xl font-semibold mt-1 tracking-tight">{value}</div>
  </div>
);

const PayStat = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-[#EAE5D9] rounded-md px-3 py-2 flex items-center gap-2" data-testid={testid}>
    <Icon className={`w-4 h-4 ${accent}`} />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">{label}</div>
      <div className="text-sm font-semibold truncate">{value}</div>
    </div>
  </div>
);

const FUNNEL_COLORS = ["#4299E1", "#DD6B20", "#805AD5", "#C05621", "#38A169", "#718096"];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState({ products: [], ingredients: [] });
  const [summary, setSummary] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [ho, setHo] = useState(null);
  const [branch, setBranch] = useState(null);

  const isHO = !user?.branch_id || ["super_admin", "admin"].includes(user?.role);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/inventory/low-stock").then((r) => setLowStock(r.data)).catch(() => {});
    if (isHO) {
      api.get("/dashboard/ho").then((r) => setHo(r.data)).catch(() => {});
    }
    api.get("/dashboard/branch").then((r) => setBranch(r.data)).catch(() => {});
  }, [user?.branch_id, user?.role]);

  const runAiSummary = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post("/reports/ai-summary");
      setSummary(data.summary);
      toast.success("AI summary generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate summary");
    } finally {
      setLoadingAi(false);
    }
  };

  if (!stats && !branch && !ho) return <div className="text-[#8B7355] text-sm">Loading dashboard…</div>;
  // Safe defaults if stats missing (e.g. permission-limited user)
  const s = stats || { total_revenue: 0, total_collected: 0, outstanding: 0, total_leads: 0, new_leads: 0, total_customers: 0, pending_followups: 0, open_tasks: 0, won_leads: 0, revenue_series: [], lead_funnel: [], employee_performance: [] };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Overview</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Dashboard
          </h1>
        </div>
        <Button
          onClick={runAiSummary}
          disabled={loadingAi}
          data-testid="dashboard-ai-summary-btn"
          className="bg-[#2C1810] hover:bg-[#3a2418] text-white"
        >
          {loadingAi ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          AI Summary
        </Button>
      </div>

      {summary && (
        <div className="border border-[#F6AD55] bg-[#FEEBC8]/50 rounded-lg p-5" data-testid="dashboard-ai-summary">
          <div className="uc-label !text-[#7B341E] flex items-center gap-2 mb-2">
            <Sparkles className="w-3 h-3" /> AI Executive Summary
          </div>
          <p className="text-sm text-[#2C1810] leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Branch snapshot (today) — visible to everyone */}
      {branch && (
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="dashboard-branch-snapshot">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-[#C05621]" />
              <div>
                <div className="uc-label">Today at {branch.branch_id ? "your branch" : "all branches"}</div>
                <div className="font-[Outfit] text-lg font-semibold tracking-tight">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
              </div>
            </div>
            <Link to="/app/pos" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="dashboard-open-pos">
              <Zap className="w-3.5 h-3.5" /> Open POS
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Today's Sales" value={inr(branch.todays_sales)} icon={IndianRupee} testid="branch-todays-sales" />
            <MiniStat label="Transactions" value={branch.sales_count} icon={TrendingUp} testid="branch-txn-count" />
            <MiniStat label="Customers" value={branch.todays_customers} icon={Users} testid="branch-customers" />
            <MiniStat label="Pending Orders" value={branch.pending_orders} icon={ListTodo} testid="branch-pending" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <PayStat label="Cash" value={inr(branch.collections?.cash)} icon={Banknote} accent="text-emerald-700" testid="branch-cash" />
            <PayStat label="UPI / GPay" value={inr(branch.collections?.upi)} icon={Smartphone} accent="text-purple-700" testid="branch-upi" />
            <PayStat label="Card" value={inr(branch.collections?.card)} icon={CreditCard} accent="text-slate-700" testid="branch-card" />
            <PayStat label="Credit" value={inr(branch.collections?.credit)} icon={AlertTriangle} accent="text-red-700" testid="branch-credit" />
          </div>
        </div>
      )}

      {/* Head Office consolidated (only HO / Super Admin) */}
      {isHO && ho && ho.total_txns > 0 && (
        <div className="bg-[#2C1810] text-white rounded-lg p-5" data-testid="dashboard-ho-block">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="uc-label !text-[#C8B8A0]">Head Office · Live today</div>
              <div className="font-[Outfit] text-2xl font-semibold tracking-tight mt-1">
                {inr(ho.total_today)}
                <span className="ml-2 text-sm font-normal text-[#C8B8A0]">across {ho.branches?.filter((b) => b.todays_sales > 0).length || 0} branches ({ho.total_txns} txn)</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <div className="uc-label !text-[#C8B8A0] mb-2">Top branches today</div>
              <ul className="space-y-1.5">
                {ho.branches?.filter((b) => b.todays_sales > 0).slice(0, 5).map((b) => (
                  <li key={b.branch_id} className="flex items-center justify-between bg-[#3a2418] rounded-md px-3 py-2 text-sm" data-testid={`ho-branch-${b.branch_id}`}>
                    <div>
                      <div className="font-semibold">{b.branch_name}</div>
                      <div className="text-[10px] text-[#C8B8A0] uppercase tracking-widest">{b.branch_code} · {b.todays_count} txn</div>
                    </div>
                    <div className="font-mono">{inr(b.todays_sales)}</div>
                  </li>
                ))}
                {ho.branches?.filter((b) => b.todays_sales > 0).length === 0 && (
                  <li className="text-[#C8B8A0] text-sm">No sales recorded today yet.</li>
                )}
              </ul>
            </div>
            <div>
              <div className="uc-label !text-[#C8B8A0] mb-2">Best sellers today</div>
              <ul className="space-y-1.5">
                {ho.best_sellers?.slice(0, 5).map((p) => (
                  <li key={p.product_id} className="flex items-center justify-between bg-[#3a2418] rounded-md px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[10px] text-[#C8B8A0] uppercase tracking-widest">{p.quantity} units</div>
                    </div>
                    <div className="font-mono">{inr(p.revenue)}</div>
                  </li>
                ))}
                {(!ho.best_sellers || ho.best_sellers.length === 0) && <li className="text-[#C8B8A0] text-sm">—</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {isHO && ho && (ho.stock_overview || ho.discrepancies) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dashboard-ho-stock-overview">
          <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
            <div className="uc-label mb-2">HO Warehouse Stock</div>
            <div className="font-[Outfit] text-2xl font-semibold" data-testid="ho-warehouse-qty">{ho.stock_overview?.warehouse_qty ?? 0}</div>
            <div className="text-xs text-[#8B7355] mt-1">units currently held centrally</div>
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
            <div className="uc-label mb-2">In Transit</div>
            <div className="font-[Outfit] text-2xl font-semibold text-amber-700" data-testid="ho-in-transit-qty">{ho.stock_overview?.in_transit_qty ?? 0}</div>
            <div className="text-xs text-[#8B7355] mt-1">dispatched, awaiting GRN acceptance</div>
          </div>
          <div className={`border rounded-lg p-4 ${ho.discrepancies?.count > 0 ? "bg-red-50 border-red-200" : "bg-white border-[#EAE5D9]"}`} data-testid="dashboard-ho-discrepancies">
            <div className="uc-label mb-2 flex items-center gap-1.5">
              {ho.discrepancies?.count > 0 && <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
              GRN Discrepancies
            </div>
            <div className={`font-[Outfit] text-2xl font-semibold ${ho.discrepancies?.count > 0 ? "text-red-700" : ""}`} data-testid="ho-discrepancy-count">
              {ho.discrepancies?.count ?? 0}
            </div>
            {ho.discrepancies?.recent?.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {ho.discrepancies.recent.slice(0, 3).map((g) => (
                  <li key={g.id} className="text-red-700">{g.number} · {g.from_branch_name} → {g.to_branch_name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isHO && ho?.stock_overview?.showroom_stock?.length > 0 && (
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4" data-testid="dashboard-ho-showroom-stock">
          <div className="uc-label mb-3">Stock at each showroom</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ho.stock_overview.showroom_stock.map((s) => (
              <div key={s.branch_id} className="bg-[#FDFBF7] border border-[#EAE5D9] rounded-md p-3" data-testid={`ho-showroom-stock-${s.branch_id}`}>
                <div className="text-xs font-semibold truncate">{s.branch_name}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="font-[Outfit] text-lg font-semibold">{s.total_qty}</span>
                  {s.ownership_type === "franchisee" && <Badge className="border-0 bg-[#C05621] text-white text-[9px] px-1.5">Franchisee</Badge>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isHO && ho?.has_franchisee && (() => { const fm = ho.franchisee_split_month || {}; const ft = ho.franchisee_split_today || {}; return (
        <div className="bg-[#FEEBC8]/40 border border-[#C05621]/30 rounded-lg p-4" data-testid="dashboard-franchisee-split">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <div className="uc-label">Franchisee Revenue Split — {fm.period || "This Month"}</div>
            <div className="text-[11px] text-[#8B7355]" data-testid="dashboard-franchisee-split-count">{fm.sale_count || 0} franchise bill{fm.sale_count === 1 ? "" : "s"} · today {inr(ft.net_amount || 0)} net</div>
          </div>
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-[10px] uppercase text-[#8B7355]">Net Sales (excl. GST)</div>
              <div className="font-[Outfit] text-xl font-semibold" data-testid="dashboard-franchisee-net">{inr(fm.net_amount || 0)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-[#8B7355]">Franchisee Share</div>
              <div className="font-[Outfit] text-xl font-semibold text-emerald-700" data-testid="dashboard-franchisee-share">{inr(fm.investor_share || 0)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-[#8B7355]">Owner (HO) Share</div>
              <div className="font-[Outfit] text-xl font-semibold text-[#C05621]" data-testid="dashboard-owner-share">{inr(fm.ho_share || 0)}</div>
            </div>
          </div>
        </div>
      ); })()}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard testid="stat-revenue" label="Total Revenue" value={inr(s.total_revenue)} icon={IndianRupee} hint={`${inr(s.total_collected)} collected`} accent="bg-emerald-100 text-emerald-700" />
        <StatCard testid="stat-outstanding" label="Outstanding" value={inr(s.outstanding)} icon={TrendingUp} hint="Unpaid + Partial" accent="bg-red-100 text-red-700" />
        <StatCard testid="stat-leads" label="Total Leads" value={s.total_leads} icon={UserPlus} hint={`${s.new_leads} new`} accent="bg-blue-100 text-blue-700" />
        <StatCard testid="stat-customers" label="Customers" value={s.total_customers} icon={Users} accent="bg-[#FEEBC8] text-[#C05621]" />
        <StatCard testid="stat-followups" label="Pending Follow-ups" value={s.pending_followups} icon={CalendarClock} accent="bg-amber-100 text-amber-700" />
        <StatCard testid="stat-tasks" label="Open Tasks" value={s.open_tasks} icon={ListTodo} accent="bg-purple-100 text-purple-700" />
        <StatCard testid="stat-won" label="Won Deals" value={s.won_leads} icon={TrendingUp} accent="bg-emerald-100 text-emerald-700" />
        <StatCard testid="stat-conversion" label="Conversion" value={`${s.total_leads ? Math.round((s.won_leads / s.total_leads) * 100) : 0}%`} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-[#EAE5D9]">
          <CardHeader>
            <CardTitle className="font-[Outfit] text-lg">Revenue — Last 6 months</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <DeferredChart height="100%">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={s.revenue_series}>
                  <CartesianGrid stroke="#EAE5D9" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#8B7355" fontSize={12} />
                  <YAxis stroke="#8B7355" fontSize={12} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EAE5D9", borderRadius: 6 }} formatter={(v) => inr(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#C05621" strokeWidth={2.5} dot={{ r: 4, fill: "#C05621" }} />
                </LineChart>
              </ResponsiveContainer>
            </DeferredChart>
          </CardContent>
        </Card>

        <Card className="border-[#EAE5D9]">
          <CardHeader>
            <CardTitle className="font-[Outfit] text-lg">Lead Funnel</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <DeferredChart height="100%">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                <Pie
                  data={s.lead_funnel}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {s.lead_funnel.map((_, i) => (
                    <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EAE5D9", borderRadius: 6 }} />
              </PieChart>
            </ResponsiveContainer>
            </DeferredChart>
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              {s.lead_funnel.map((f, i) => (
                <div key={f.status} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }} />
                  <span className="capitalize text-[#5C4033]">{f.status}</span>
                  <span className="ml-auto font-medium">{f.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2">
          <CardTitle className="font-[Outfit] text-lg">Employee Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {s.employee_performance?.length === 0 ? (
            <p className="text-sm text-[#8B7355]">No employees yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left uc-label border-b border-[#EAE5D9]">
                    <th className="py-2">Name</th>
                    <th className="py-2">Role</th>
                    <th className="py-2 text-right">Leads Assigned</th>
                    <th className="py-2 text-right">Deals Won</th>
                  </tr>
                </thead>
                <tbody>
                  {s.employee_performance.map((p, i) => (
                    <tr key={i} className="border-b border-[#EAE5D9] last:border-0">
                      <td className="py-2.5 font-medium">{p.name}</td>
                      <td className="py-2.5"><Badge variant="outline" className="capitalize">{p.role.replace("_", " ")}</Badge></td>
                      <td className="py-2.5 text-right">{p.assigned}</td>
                      <td className="py-2.5 text-right font-semibold text-emerald-700">{p.won}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(lowStock.products.length > 0 || lowStock.ingredients.length > 0) && (
        <Card className="border-red-200 bg-red-50/40" data-testid="dashboard-low-stock">
          <CardHeader className="pb-2">
            <CardTitle className="font-[Outfit] text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" /> Low-stock alerts ({lowStock.products.length + lowStock.ingredients.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="uc-label mb-2">Products</div>
                {lowStock.products.length ? (
                  <ul className="space-y-1 text-sm">
                    {lowStock.products.map((p) => (
                      <li key={p.id} className="flex justify-between border-b border-red-100 py-1"><span>{p.name}</span><span className="font-semibold text-red-700">{p.current_stock} {p.unit}</span></li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-[#8B7355]">All good.</p>}
              </div>
              <div>
                <div className="uc-label mb-2">Ingredients</div>
                {lowStock.ingredients.length ? (
                  <ul className="space-y-1 text-sm">
                    {lowStock.ingredients.map((p) => (
                      <li key={p.id} className="flex justify-between border-b border-red-100 py-1"><span>{p.name}</span><span className="font-semibold text-red-700">{p.current_stock} {p.unit}</span></li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-[#8B7355]">All good.</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
